import type { Point } from '../state/types';
import { pointToLineDistance } from '../geometry/pointDistance';
import { RECOGNITION_CONFIG } from './config';

export interface BoundingBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
  center: Point;
}

export const getBoundingBox = (points: Point[]): BoundingBox => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
    center: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 },
  };
};

export const perpendicularDistance = (point: Point, lineStart: Point, lineEnd: Point): number => {
  return pointToLineDistance(point, lineStart, lineEnd, false);
};

export const simplifyPoints = (
  points: Point[],
  tolerance = RECOGNITION_CONFIG.simplifyTolerance
): Point[] => {
  if (points.length < 3) return points;

  const first = points[0]!;
  const last = points[points.length - 1]!;

  let maxDist = 0;
  let maxIndex = 0;

  for (let i = 1; i < points.length - 1; i++) {
    const dist = perpendicularDistance(points[i]!, first, last);
    if (dist > maxDist) {
      maxDist = dist;
      maxIndex = i;
    }
  }

  if (maxDist > tolerance) {
    const left = simplifyPoints(points.slice(0, maxIndex + 1), tolerance);
    const right = simplifyPoints(points.slice(maxIndex), tolerance);
    return [...left.slice(0, -1), ...right];
  }

  return [first, last];
};

export const isClosedShape = (points: Point[], bounds?: BoundingBox): boolean => {
  if (points.length < 3) return false;

  const first = points[0]!;
  const last = points[points.length - 1]!;
  const distance = Math.hypot(last.x - first.x, last.y - first.y);
  const computedBounds = bounds ?? getBoundingBox(points);
  const diagonal = Math.hypot(computedBounds.width, computedBounds.height);

  if (distance < diagonal * RECOGNITION_CONFIG.closedShapeDiagonalFactor) {
    return true;
  }

  const checkCount = Math.max(
    1,
    Math.floor(points.length * RECOGNITION_CONFIG.closedShapeTailFactor)
  );
  for (let i = points.length - checkCount; i < points.length; i++) {
    const p = points[i]!;
    const distToStart = Math.hypot(p.x - first.x, p.y - first.y);
    if (distToStart < diagonal * RECOGNITION_CONFIG.closedShapeTailDistanceFactor) {
      return true;
    }
  }

  return false;
};

export const getAngle = (p1: Point, vertex: Point, p2: Point): number => {
  const v1 = { x: p1.x - vertex.x, y: p1.y - vertex.y };
  const v2 = { x: p2.x - vertex.x, y: p2.y - vertex.y };

  const dot = v1.x * v2.x + v1.y * v2.y;
  const cross = v1.x * v2.y - v1.y * v2.x;

  const angle = Math.atan2(Math.abs(cross), dot);
  return (angle * 180) / Math.PI;
};

export const findCorners = (
  points: Point[],
  bounds?: BoundingBox,
  angleThreshold = RECOGNITION_CONFIG.cornerAngleThreshold
): Point[] => {
  const corners: Point[] = [];
  const candidateCorners: Array<{ point: Point; angle: number; index: number }> = [];

  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1]!;
    const curr = points[i]!;
    const next = points[i + 1]!;

    const angle = getAngle(prev, curr, next);
    if (angle < 180 - angleThreshold) {
      candidateCorners.push({ point: curr, angle, index: i });
    }
  }

  if (points.length > 2) {
    const last = points[points.length - 1]!;
    const first = points[0]!;
    const second = points[1]!;

    if (Math.hypot(last.x - first.x, last.y - first.y) < RECOGNITION_CONFIG.firstCornerDistance) {
      const angle = getAngle(last, first, second);
      if (angle < 180 - angleThreshold) {
        candidateCorners.unshift({ point: first, angle, index: 0 });
      }
    }
  }

  const computedBounds = bounds ?? getBoundingBox(points);
  const mergeDistance =
    Math.max(computedBounds.width, computedBounds.height) *
    RECOGNITION_CONFIG.cornerMergeDistanceFactor;

  const merged: boolean[] = new Array(candidateCorners.length).fill(false);
  for (let i = 0; i < candidateCorners.length; i++) {
    if (merged[i]) continue;

    let bestCorner = candidateCorners[i]!;
    for (let j = i + 1; j < candidateCorners.length; j++) {
      if (merged[j]) continue;

      const dist = Math.hypot(
        candidateCorners[j]!.point.x - bestCorner.point.x,
        candidateCorners[j]!.point.y - bestCorner.point.y
      );

      if (dist < mergeDistance) {
        if (candidateCorners[j]!.angle < bestCorner.angle) {
          bestCorner = candidateCorners[j]!;
        }
        merged[j] = true;
      }
    }
    corners.push(bestCorner.point);
  }

  return corners;
};

export const calculateEdgeScore = (points: Point[], bounds: BoundingBox): number => {
  const edgeThreshold =
    Math.max(bounds.width, bounds.height) * RECOGNITION_CONFIG.edgeThresholdFactor;
  let pointsNearEdge = 0;

  for (const p of points) {
    const distToLeft = Math.abs(p.x - bounds.minX);
    const distToRight = Math.abs(p.x - bounds.maxX);
    const distToTop = Math.abs(p.y - bounds.minY);
    const distToBottom = Math.abs(p.y - bounds.maxY);

    const minDist = Math.min(distToLeft, distToRight, distToTop, distToBottom);
    if (minDist < edgeThreshold) {
      pointsNearEdge++;
    }
  }

  return pointsNearEdge / points.length;
};
